import React, { useState, useEffect, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import Panel from './navigation/Panel';
import AddSiteModal from '../features/settings/sites/AddSiteModal';
import { useSites } from '../contexts/SitesContext';

import { ReactComponent as Logo } from '../assets/icons/logo.svg';
import { ReactComponent as MenuIcon } from '../assets/icons/menu2.svg';
import { ReactComponent as XIcon } from '../assets/icons/x.svg';
import { ReactComponent as Spinner } from '../assets/icons/spinner.svg';

const MOBILE_LAYOUT_BREAKPOINT = 850;
const COMPACT_SIDEBAR_BREAKPOINT = 1200;

const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => (
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  ));
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mql = window.matchMedia(query);
    const handler = (event) => setMatches(event.matches);
    setMatches(mql.matches);
    if (mql.addEventListener) mql.addEventListener('change', handler);
    else if (mql.addListener) mql.addListener(handler);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler);
      else if (mql.removeListener) mql.removeListener(handler);
    };
  }, [query]);
  return matches;
};

const DashboardLayout = ({ bootLoading = false }) => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [isNavVisible, setNavVisible] = useState(false);
  const [isMobileHeaderCondensed, setIsMobileHeaderCondensed] = useState(false);
  const [desktopSidebarOverride, setDesktopSidebarOverride] = useState(null);
  const { getSites } = useSites();
  const [addSiteModalVisible, setAddSiteModalVisible] = useState(false);
  const toggleNav = useCallback(() => { setNavVisible((visible) => !visible); }, []);
  const closeNav = useCallback(() => { setNavVisible(false); }, []);
  const isMobileLayout = useMediaQuery(`(max-width: ${MOBILE_LAYOUT_BREAKPOINT}px)`);
  const isCompactDesktopRange = useMediaQuery(
    `(min-width: ${MOBILE_LAYOUT_BREAKPOINT + 1}px) and (max-width: ${COMPACT_SIDEBAR_BREAKPOINT}px)`
  );
  const shouldAutoUseCompactSidebar = !isMobileLayout && isCompactDesktopRange;
  const isCompactSidebar = !isMobileLayout && (
    desktopSidebarOverride === null ? shouldAutoUseCompactSidebar : desktopSidebarOverride === 'compact'
  );
  const showDesktopSidebarHandle = !isMobileLayout;

  useEffect(() => {
    if (!bootLoading && getSites().length === 0) {
      setAddSiteModalVisible(true);
    }
  }, [bootLoading, getSites]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const shouldLock = isMobileLayout && isNavVisible;
    document.body.classList.toggle('nav-open', shouldLock);
    document.documentElement.classList.toggle('nav-open', shouldLock);
    return () => {
      document.body.classList.remove('nav-open');
      document.documentElement.classList.remove('nav-open');
    };
  }, [isNavVisible, isMobileLayout]);

  useEffect(() => {
    if (!isMobileLayout && isNavVisible) {
      setNavVisible(false);
    }
  }, [isMobileLayout, isNavVisible]);

  useEffect(() => {
    if (isMobileLayout) {
      setDesktopSidebarOverride(null);
    }
  }, [isMobileLayout]);

  useEffect(() => {
    if (!isMobileLayout) {
      setIsMobileHeaderCondensed(false);
      return undefined;
    }

    const updateHeaderState = () => {
      const scrollY = window.scrollY;
      setIsMobileHeaderCondensed((current) => (current ? scrollY > 8 : scrollY > 24));
    };

    updateHeaderState();
    window.addEventListener('scroll', updateHeaderState, { passive: true });
    window.addEventListener('resize', updateHeaderState);
    window.addEventListener('orientationchange', updateHeaderState);

    return () => {
      window.removeEventListener('scroll', updateHeaderState);
      window.removeEventListener('resize', updateHeaderState);
      window.removeEventListener('orientationchange', updateHeaderState);
    };
  }, [isMobileLayout]);

  const toggleDesktopSidebar = useCallback(() => {
    setDesktopSidebarOverride((currentOverride) => {
      const isCurrentlyCompact = currentOverride === null
        ? shouldAutoUseCompactSidebar
        : currentOverride === 'compact';

      return isCurrentlyCompact ? 'expanded' : 'compact';
    });
  }, [shouldAutoUseCompactSidebar]);

  const layoutClasses = [
    'dashboard-layout',
    isDarkMode ? 'dark-theme' : 'light-theme',
    isMobileLayout ? 'mobile-layout' : '',
    isMobileHeaderCondensed ? 'mobile-layout--condensed' : '',
    isCompactSidebar ? 'sidebar-collapsed' : ''
  ].filter(Boolean).join(' ');

  const sidebarClasses = [
    'sidebar',
    isNavVisible ? 'active' : '',
    isCompactSidebar ? 'collapsed' : ''
  ].filter(Boolean).join(' ');

  return (
    <div className={layoutClasses}>
      <div className="nav-mobile">
        <div className={`nav-mobile__bar${isNavVisible ? ' is-active' : ''}`}>
          <Logo className="logo" onClick={() => navigate("/")} />
          <button
            type="button"
            className="nav-mobile__toggle"
            aria-label={isNavVisible ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={isNavVisible}
            onClick={toggleNav}
          >
            <MenuIcon className="icon md hamburger" />
            <XIcon className="icon md close" />
          </button>
        </div>
      </div>

      <div className={sidebarClasses}>
        <Panel isNavVisible={isNavVisible}
               closeNav={closeNav}
               setAddSiteModalVisible={setAddSiteModalVisible}
               isCompact={isCompactSidebar}
               isMobile={isMobileLayout} />
        {showDesktopSidebarHandle && (
          <button
            type="button"
            className={`sidebar-edge-toggle${isCompactSidebar ? ' is-compact' : ' is-expanded'}`}
            onClick={toggleDesktopSidebar}
            aria-label={isCompactSidebar ? 'Expand sidebar' : 'Collapse sidebar'}
            title={isCompactSidebar ? 'Expand sidebar' : 'Collapse sidebar'}
          />
        )}
      </div>

      {addSiteModalVisible && (
        <AddSiteModal setAddSiteModalVisible={setAddSiteModalVisible}
                      existingNames={getSites().map(site => site.website)} />
      )}

      <main>
        {bootLoading ? (
          <div className="page-shell page-shell--loading">
            <div className="page-loading" role="status" aria-live="polite">
              <div className="spinner">
                <Spinner />
                <p>Loading data...</p>
              </div>
            </div>
          </div>
        ) : (
          <Outlet />
        )}
      </main>
    </div>
  );
};

export default DashboardLayout;
